import React, { useState } from 'react';
import { 
    DataGrid, 
    GridActionsCellItem, 
    GridRowModes
} from '@mui/x-data-grid';
import { FaEdit, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import { Alert, Collapse } from '@mui/material';

import { GRID_CONFIG } from '../constants';

const ConfigsDataGrid = ({
    agentConfigs,
    loading,
    rowModesModel,
    onRowModesModelChange,
    onRowEditStop,
    onProcessRowUpdate,
    onEditClick,
    onSaveClick,
    onCancelClick,
    onDeleteClick
}) => {
    const [validationErrors, setValidationErrors] = useState({});
    const [editingRows, setEditingRows] = useState({}); // Зберігаємо зміни під час редагування

    // Функція для валідації всіх полів рядка з актуальними даними
    const validateRow = (rowData = null) => {
        const dataToValidate = rowData;

        if (!dataToValidate) return {};

        const errors = {};

        // Валідація температури
        if (dataToValidate.temperature !== undefined && dataToValidate.temperature !== null && dataToValidate.temperature !== '') {
            const temp = parseFloat(dataToValidate.temperature);
            //console.log('row.temperature validation :>> ', temp);
            if (isNaN(temp) || temp < 0 || temp > 2) {
                errors.temperature = 'Температура повинна бути числом від 0 до 2';
            }
        }

        // Валідація top_k
        if (dataToValidate.top_k !== undefined && dataToValidate.top_k !== null && dataToValidate.top_k !== '') {
            const topK = parseInt(dataToValidate.top_k);
            if (isNaN(topK) || topK < 1) {
                errors.top_k = 'Top K повинно бути цілим числом більше 0';
            }
        }

        // Валідація top_k_notes
        if (dataToValidate.top_k_notes !== undefined && dataToValidate.top_k_notes !== null && dataToValidate.top_k_notes !== '') {
            const topKNotes = parseInt(dataToValidate.top_k_notes);
            if (isNaN(topKNotes) || topKNotes < 1) {
                errors.top_k_notes = 'Top K нотаток повинно бути цілим числом більше 0';
            }
        }

        // Валідація top_k_module_docs
        if (dataToValidate.top_k_module_docs !== undefined && dataToValidate.top_k_module_docs !== null && dataToValidate.top_k_module_docs !== '') {
            const topKModuleDocs = parseInt(dataToValidate.top_k_module_docs);
            if (isNaN(topKModuleDocs) || topKModuleDocs < 1) {
                errors.top_k_module_docs = 'Top K модульних документів повинно бути цілим числом більше 0';
            }
        }

        // Валідація top_k_submodule_docs
        if (dataToValidate.top_k_submodule_docs !== undefined && dataToValidate.top_k_submodule_docs !== null && dataToValidate.top_k_submodule_docs !== '') {
            const topKSubmoduleDocs = parseInt(dataToValidate.top_k_submodule_docs);
            if (isNaN(topKSubmoduleDocs) || topKSubmoduleDocs < 1) {
                errors.top_k_submodule_docs = 'Top K підмодульних документів повинно бути цілим числом більше 0';
            }
        }

        // Валідація max_response_tokens
        if (dataToValidate.max_response_tokens !== undefined && dataToValidate.max_response_tokens !== null && dataToValidate.max_response_tokens !== '') {
            const maxTokens = parseInt(dataToValidate.max_response_tokens);
            if (isNaN(maxTokens) || maxTokens < 1 || maxTokens > 32768) {
                errors.max_response_tokens = 'Макс. токенів повинно бути цілим числом від 1 до 32768';
            }
        }

        // Валідація planner_confidence_threshold
        if (dataToValidate.planner_confidence_threshold !== undefined && dataToValidate.planner_confidence_threshold !== null && dataToValidate.planner_confidence_threshold !== '') {
            const val = parseFloat(dataToValidate.planner_confidence_threshold);
            if (isNaN(val) || val < 0 || val > 1) {
                errors.planner_confidence_threshold = 'Поріг впевненості планувальника повинен бути числом від 0 до 1';
            }
        }

        // Валідація synth_tokens_threshold
        if (dataToValidate.synth_tokens_threshold !== undefined && dataToValidate.synth_tokens_threshold !== null && dataToValidate.synth_tokens_threshold !== '') {
            const val = parseInt(dataToValidate.synth_tokens_threshold);
            if (isNaN(val) || val < 1) {
                errors.synth_tokens_threshold = 'Поріг токенів синтезу повинен бути цілим числом більше 0';
            }
        }

        // Валідація synth_sources_threshold
        if (dataToValidate.synth_sources_threshold !== undefined && dataToValidate.synth_sources_threshold !== null && dataToValidate.synth_sources_threshold !== '') {
            const val = parseInt(dataToValidate.synth_sources_threshold);
            if (isNaN(val) || val < 1) {
                errors.synth_sources_threshold = 'Поріг джерел синтезу повинен бути цілим числом більше 0';
            }
        }

        // Валідація synth_docs_threshold
        if (dataToValidate.synth_docs_threshold !== undefined && dataToValidate.synth_docs_threshold !== null && dataToValidate.synth_docs_threshold !== '') {
            const val = parseInt(dataToValidate.synth_docs_threshold);
            if (isNaN(val) || val < 1) {
                errors.synth_docs_threshold = 'Поріг документів синтезу повинен бути цілим числом більше 0';
            }
        }

        // Валідація synth_subqueries_threshold
        if (dataToValidate.synth_subqueries_threshold !== undefined && dataToValidate.synth_subqueries_threshold !== null && dataToValidate.synth_subqueries_threshold !== '') {
            const val = parseInt(dataToValidate.synth_subqueries_threshold);
            if (isNaN(val) || val < 1) {
                errors.synth_subqueries_threshold = 'Поріг підзапитів синтезу повинен бути цілим числом більше 0';
            }
        }

        return errors;
    };

    // Обробник збереження з валідацією актуальних даних
    const handleProcessRowUpdate = (newRow, oldRow) => {
        // Перевіряємо чи є локальні зміни для цього рядка
        const localChanges = editingRows[newRow.id] || {};
        
        // Створюємо повний об'єкт з всіма змінами
        const completeRow = {
            ...oldRow,
            ...localChanges,
            ...newRow // Поточні зміни мають найвищий пріоритет
        };
        
        const errors = validateRow(completeRow);
        
        if (Object.keys(errors).length > 0) {
            setValidationErrors({ ...validationErrors, [completeRow.id]: errors });
            // Повертаємо старий рядок, щоб скасувати оновлення
            return oldRow;
        }

        // Очищуємо помилки валідації для цього рядка
        const newErrors = { ...validationErrors };
        delete newErrors[completeRow.id];
        setValidationErrors(newErrors);

        // Очищуємо локальні зміни для цього рядка
        const newEditingRows = { ...editingRows };
        delete newEditingRows[completeRow.id];
        setEditingRows(newEditingRows);

        // Викликаємо оригінальний обробник з повним об'єктом
        return onProcessRowUpdate(completeRow, oldRow);
    };

    // Обробник збереження з валідацією (для кнопки Save)
    const handleSaveClick = (id) => () => {
        // Отримуємо базові дані рядка
        const currentRow = agentConfigs.find(config => config.id === id);
        // Додаємо поточні зміни з локального стану
        const currentEditData = editingRows[id] || {};
        const completeRowData = { ...currentRow, ...currentEditData };
        
        const errors = validateRow(completeRowData);
        //console.log('save click errors :>> ', errors);
        
        if (Object.keys(errors).length > 0) {
            // Також зберігаємо в validationErrors для можливості подальшого використання
            setValidationErrors({ ...validationErrors, [id]: errors });
            return; // Не зберігаємо, якщо є помилки
        }

        // Очищуємо помилки валідації для цього рядка
        const newErrors = { ...validationErrors };
        delete newErrors[id];
        setValidationErrors(newErrors);

        // Викликаємо оригінальний обробник збереження
        onSaveClick(id)();
    };

    // Обробник скасування з очищенням помилок
    const handleCancelClick = (id) => () => {
        // Очищуємо помилки валідації для цього рядка
        const newErrors = { ...validationErrors };
        delete newErrors[id];
        setValidationErrors(newErrors);
        
        // Очищуємо локальні зміни для цього рядка
        const newEditingRows = { ...editingRows };
        delete newEditingRows[id];
        setEditingRows(newEditingRows);
        
        // Викликаємо оригінальний обробник скасування
        onCancelClick(id)();
    };
    // Обробник початку редагування
    const handleEditClick = (id) => () => {
        // Ініціалізуємо локальний стан для редагування
        const currentRow = agentConfigs.find(config => config.id === id);
        setEditingRows(prev => ({
            ...prev,
            [id]: { ...currentRow }
        }));
        
        // Викликаємо оригінальний обробник
        onEditClick(id)();
    };

    const columns = [
        { 
            field: 'name', 
            headerName: 'Назва', 
            width: 300,
            editable: false  // Ім'я змінювати не можна
        },
        { 
            field: 'chat_model', 
            headerName: 'Модель чату', 
            width: 200,
            editable: true
        },
        { 
            field: 'temperature', 
            headerName: 'Температура', 
            type: 'number', 
            width: 120,
            editable: true,
            valueFormatter: (value) => {
                if (!value) {
                    return value || '';
                }
                const num = parseFloat(value);
                return isNaN(num) ? value : num.toFixed(2);
            }
        },
        { 
            field: 'top_k', 
            headerName: 'Top K', 
            type: 'number', 
            width: 120,
            editable: true,
            valueFormatter: (value) => {
                if (!value) {
                    return value || '';
                }
                const num = parseInt(value);
                return isNaN(num) ? value : num.toString();
            }
        },
        { 
            field: 'top_k_notes', 
            headerName: 'Top K нотаток', 
            type: 'number', 
            width: 120,
            editable: true,
            valueFormatter: (value) => {
                if (!value) {
                    return value || '';
                }
                const num = parseInt(value);
                return isNaN(num) ? value : num.toString();
            }
        },
        { 
            field: 'top_k_module_docs', 
            headerName: 'Top K module docs', 
            type: 'number', 
            width: 150,
            editable: true,
            valueFormatter: (value) => {
                if (!value) {
                    return value || '';
                }
                const num = parseInt(value);
                return isNaN(num) ? value : num.toString();
            }
        },
        { 
            field: 'top_k_submodule_docs', 
            headerName: 'Top K submodule docs', 
            type: 'number', 
            width: 170,
            editable: true,
            valueFormatter: (value) => {
                if (!value) {
                    return value || '';
                }
                const num = parseInt(value);
                return isNaN(num) ? value : num.toString();
            }
        },
        { 
            field: 'max_response_tokens', 
            headerName: 'Макс. токенів', 
            type: 'number', 
            width: 120,
            editable: true,
            valueFormatter: (value) => {
                if (!value) {
                    return value || '';
                }
                const num = parseInt(value);
                return isNaN(num) ? value : value;
            }
        },
        { 
            field: 'planner_confidence_threshold', 
            headerName: 'Поріг впевненості', 
            type: 'number', 
            width: 160,
            editable: true,
            valueFormatter: (value) => {
                if (value === undefined || value === null || value === '') {
                    return value || '';
                }
                const num = parseFloat(value);
                return isNaN(num) ? value : num.toFixed(2);
            }
        },
        { 
            field: 'synth_tokens_threshold', 
            headerName: 'Поріг токенів синтезу', 
            type: 'number', 
            width: 180,
            editable: true,
            valueFormatter: (value) => {
                if (value === undefined || value === null || value === '') {
                    return value || '';
                }
                const num = parseInt(value);
                return isNaN(num) ? value : num.toString();
            }
        },
        { 
            field: 'synth_sources_threshold', 
            headerName: 'Поріг джерел синтезу', 
            type: 'number', 
            width: 180,
            editable: true,
            valueFormatter: (value) => {
                if (value === undefined || value === null || value === '') {
                    return value || '';
                }
                const num = parseInt(value);
                return isNaN(num) ? value : num.toString();
            }
        },
        { 
            field: 'synth_docs_threshold', 
            headerName: 'Поріг документів синтезу', 
            type: 'number', 
            width: 190,
            editable: true,
            valueFormatter: (value) => {
                if (value === undefined || value === null || value === '') {
                    return value || '';
                }
                const num = parseInt(value);
                return isNaN(num) ? value : num.toString();
            }
        },
        { 
            field: 'synth_subqueries_threshold', 
            headerName: 'Поріг підзапитів синтезу', 
            type: 'number', 
            width: 190,
            editable: true,
            valueFormatter: (value) => {
                if (value === undefined || value === null || value === '') {
                    return value || '';
                }
                const num = parseInt(value);
                return isNaN(num) ? value : num.toString();
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Дії',
            width: 150,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
                const config = agentConfigs.find(c => c.id === id);
                const isDefault = config && (config.id === 1 || config.name === 'default');

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<FaSave size={18} />}
                            label="Зберегти"
                            sx={{ color: 'primary.main' }}
                            onClick={handleSaveClick(id)}
                        />, 
                        <GridActionsCellItem
                            icon={<FaTimes size={18} />}
                            label="Скасувати"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />, 
                    ];
                }

                const actions = [
                    <GridActionsCellItem
                        icon={<FaEdit size={18} />}
                        label="Редагувати"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />
                ];

                if (!isDefault) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<FaTrash size={18} />}
                            label="Видалити"
                            onClick={onDeleteClick(id)}
                            color="inherit"
                        />
                    );
                }

                return actions;
            },
        },
    ];

    // Отримуємо всі помилки валідації для рядків в режимі редагування та відображені помилки
    const getValidationErrorsForDisplay = () => {
        const editingRows = Object.keys(rowModesModel).filter(
            id => rowModesModel[id]?.mode === GridRowModes.Edit
        );
        
        // Спочатку додаємо помилки для рядків в режимі редагування
        const allErrors = editingRows.reduce((acc, rowId) => {
            if (validationErrors[rowId]) {
                acc[rowId] = validationErrors[rowId];
            }
            return acc;
        }, {});

        return allErrors;
    };

    const currentValidationErrors = getValidationErrorsForDisplay();

    return (
        <div style={{ width: '100%' }}>
            {/* Відображення помилок валідації */}
            {Object.keys(currentValidationErrors).length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                    {Object.entries(currentValidationErrors).map(([rowId, errors]) => {
                        const config = agentConfigs.find(c => c.id === parseInt(rowId));
                        return (
                            <Collapse key={rowId} in={true}>
                                <Alert 
                                    severity="error" 
                                    style={{ marginBottom: '8px' }}
                                >
                                    <div>
                                        <strong>Помилки в конфігурації "{config?.name}":</strong>
                                        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                            {Object.entries(errors).map(([field, message]) => (
                                                <li key={field}>{message}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </Alert>
                            </Collapse>
                        );
                    })}
                </div>
            )}
            
            <div style={{ height: GRID_CONFIG.HEIGHT, width: '100%' }}>
            <DataGrid
                rows={agentConfigs}
                columns={columns}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={onRowModesModelChange}
                onRowEditStop={onRowEditStop}
                processRowUpdate={handleProcessRowUpdate}
                onCellValueChange={(params, event, details) => {
                    // Відстежуємо зміни в полях під час редагування
                    const { id, field, value } = params;
                    setEditingRows(prev => ({
                        ...prev,
                        [id]: {
                            ...prev[id],
                            [field]: value
                        }
                    }));
                }}
                loading={loading}
                hideFooter
                pagination={false}
                paginationMode="client"
                pageSize={GRID_CONFIG.DEFAULT_PAGE_SIZE}
                rowsPerPageOptions={GRID_CONFIG.PAGE_SIZE_OPTIONS}
                disableSelectionOnClick
                sx={{
                    '& .actions': {
                        color: 'text.secondary',
                    },
                    '& .textPrimary': {
                        color: 'text.primary',
                    },
                }}
            />
            </div>
        </div>
    );
};

export default ConfigsDataGrid;
